import { useState, useEffect, useRef } from "react";

// ─── IFRAME HEIGHT REPORTING ───────────────────────────────────────────────
// Sends page height to the parent window so an iframe can auto-resize.
// Safe to call when not in an iframe — window.parent === window in that case.
function postHeight() {
  var h = document.documentElement.scrollHeight;
  try { window.parent.postMessage({ iframeHeight: h }, '*'); } catch(e) {}
}

// Fires immediately on mount, then polls every 200ms for 3s to catch
// images, animations and the Marketo form all settling at their final height.
function useHeightReporter(active) {
  const intervalRef = useRef(null);
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (!active) return;
    postHeight();
    var count = 0;
    intervalRef.current = setInterval(function() {
      postHeight();
      if (++count > 15) clearInterval(intervalRef.current);
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [active]);
}

const B = {
  navy:       "#071528",
  navyMid:    "#0D1B35",
  blue:       "#1A5FD4",
  blueBright: "#2563EB",
  blueLight:  "#EEF4FF",
  blueMid:    "#DBEAFE",
  textMid:    "#374151",
  textSub:    "#6B7280",
  border:     "#E5E7EB",
  white:      "#FFFFFF",
  bg:         "#F3F5FC",
};

const LOGO = "https://cdn.prod.website-files.com/69adc2a53c763eb9450db7ff/69adc2a53c763eb9450db854_LOGO.png";
const REPORT_IMG = "https://cdn.prod.website-files.com/69adc2a53c763eb9450db829/69adc2a53c763eb9450dbc85_696ee9ee99ad1d9d836a1f8c_AI%2520Report%25202026_Resource.avif";
const REPORT_URL = "https://www.litify.com/resources/state-of-ai-in-legal-report";

function LitifyLogo({ height = 32 }) {
  const [failed, setFailed] = useState(false);
  if (failed) return (
    <div style={{ height, display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ width:height, height, background:B.navy, borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{ color:"#fff", fontWeight:900, fontSize:height*0.45 }}>L</span>
      </div>
      <span style={{ fontWeight:800, fontSize:height*0.6, color:B.navy, letterSpacing:"-0.02em" }}>LITIFY</span>
    </div>
  );
  return <img src={LOGO} alt="Litify" height={height} style={{ height, display:"block", objectFit:"contain" }} onError={() => setFailed(true)} />;
}

const INDUSTRY_TASK_DATA = [
  { label:"Foundational", pct:66, desc:"Research, summarizing, basic drafting", color:"#93C5FD" },
  { label:"Advanced",     pct:22, desc:"Chronologies, conflict review, integrations", color:"#3B82F6" },
  { label:"Agentic",      pct:11, desc:"Multi-step, autonomous task delegation", color:"#1A5FD4" },
];

const questions = [
  {
    id:1, section:"Tools & Investment", sectionIcon:"💼",
    question:"Which best describes the AI tools your organization primarily uses?",
    options:[
      { text:"Free tools like ChatGPT, Copilot, or Gemini found by individuals", score:1 },
      { text:"Mix of free tools plus some paid subscriptions starting to take hold", score:2 },
      { text:"Primarily purpose-built, legal-specific tools with organizational buy-in", score:3 },
      { text:"A full AI tech stack including agentic tools, budgeted alongside headcount", score:4 },
    ],
  },
  {
    id:2, section:"Tools & Investment", sectionIcon:"💼",
    question:"How has your organization's AI investment changed over the past year?",
    options:[
      { text:"No investment — people use whatever free tools they find", score:1 },
      { text:"Some budget allocated but it's not a strategic priority yet", score:2 },
      { text:"Investment has increased meaningfully — AI is now a core budget line", score:3 },
      { text:"AI spend is evaluated in parallel with headcount and CapEx decisions", score:4 },
    ],
  },
  {
    id:3, section:"Readiness", sectionIcon:"🛡️",
    question:"Does your organization have an AI policy covering safe and approved use?",
    options:[
      { text:"No — staff use AI however they see fit", score:1 },
      { text:"Something is being drafted but nothing formal is in place yet", score:2 },
      { text:"Yes, with active policy reviews and enablement programs", score:3 },
      { text:"Fully integrated into our CISO function with annual certifications", score:4 },
    ],
  },
  {
    id:4, section:"Readiness", sectionIcon:"🛡️",
    question:"How would you describe AI training and enablement at your organization?",
    options:[
      { text:"No formal training — individuals figure it out themselves", score:1 },
      { text:"Some guidance shared but no structured program in place", score:2 },
      { text:"Active enablement programs helping staff use AI safely and effectively", score:3 },
      { text:"AI training is core to onboarding and continued employee education", score:4 },
    ],
  },
  {
    id:5, section:"Task Scope", sectionIcon:"📋",
    question:"What tasks is AI primarily being used for in your legal work?",
    options:[
      { text:"Finding information quickly, summarizing documents, basic Q&A", score:1 },
      { text:"Some advanced tasks like building chronologies or reviewing for conflicts", score:2 },
      { text:"Multi-step, process-integrated tasks embedded in daily legal workflows", score:3 },
      { text:"Entire tasks delegated to agentic AI with limited human involvement", score:4 },
    ],
  },
  {
    id:6, section:"Task Scope", sectionIcon:"📋",
    question:"How integrated is AI with your existing legal platform and case data?",
    options:[
      { text:"Not integrated — used separately from our main systems", score:1 },
      { text:"Partially — some individuals connect it to their work but it's inconsistent", score:2 },
      { text:"Embedded in our legal platform and used consistently across the team", score:3 },
      { text:"AI operates autonomously across workflows, connected to all data and stakeholders", score:4 },
    ],
  },
  {
    id:7, section:"Impact", sectionIcon:"📈",
    question:"Where is the benefit of AI being felt most in your organization?",
    options:[
      { text:"Mainly at the individual level — people work a bit faster personally", score:1 },
      { text:"Some team productivity gains but no clear business metrics yet", score:2 },
      { text:"Organizational metrics improving — we're starting to measure leading indicators", score:3 },
      { text:"Clear financial ROI — bending the cost curve or accelerating revenue", score:4 },
    ],
  },
  {
    id:8, section:"Impact", sectionIcon:"📈",
    question:"How does your organization track and measure AI's return on investment?",
    options:[
      { text:"We don't — no targets or measurement framework in place", score:1 },
      { text:"Anecdotal benefits noted but nothing formal or consistent", score:2 },
      { text:"Clear goals and leading indicators monitored on a regular basis", score:3 },
      { text:"AI ROI reported to leadership alongside financial performance metrics", score:4 },
    ],
  },
];

const levels = [
  {
    level:1, label:"Explore", range:[8,13],
    tagline:"Getting started with AI",
    description:"Individuals at your organization are experimenting with free tools for basic tasks — finding answers quickly, summarizing documents. This is where the legal industry stood in 2023, when only 23% had adopted AI. The gains are personal, not yet organizational.",
    next:"Establish a basic AI policy, identify one or two legal-specific tools for your highest-impact use cases, and set initial targets so you can start measuring progress.",
  },
  {
    level:2, label:"Expand", range:[14,19],
    tagline:"Building momentum",
    description:"You're investing and beginning to standardize, but readiness is uneven. Some teams are ahead of others, and the gap between individual productivity and business impact remains wide. This reflects the majority of the legal market today.",
    next:"Formalize your AI policy, launch structured enablement programs, and shift investment toward legal-specific tools. Set clear KPIs so ROI starts taking shape.",
  },
  {
    level:3, label:"Integrate", range:[20,27],
    tagline:"AI embedded in operations",
    description:"AI is becoming part of how your organization operates. Purpose-built tools are in use, policies are maturing, and staff are tackling more advanced multi-step tasks. Leading metrics are starting to improve. You're ahead of most of the legal market.",
    next:"Begin evaluating agentic AI for end-to-end task delegation. Ensure ROI is formally tracked and reported to leadership, and fold AI into wider financial and headcount planning.",
  },
  {
    level:4, label:"Transform", range:[28,32],
    tagline:"Operating at the frontier",
    description:"AI is fully embedded in your organization. Agentic tools handle entire workflows, ROI is measurable and meaningful, and AI strategy is woven into core business decisions. You're in the top tier of the legal industry.",
    next:"Maintain your edge by staying current with agentic AI advances, running regular CISO-level policy reviews, and keeping AI training live in onboarding and continued education.",
  },
];

const sectionMeta = {
  "Tools & Investment": { color:"#2563EB", light:"#EEF4FF" },
  "Readiness":          { color:"#7C3AED", light:"#F5F3FF" },
  "Task Scope":         { color:"#0891B2", light:"#ECFEFF" },
  "Impact":             { color:"#059669", light:"#ECFDF5" },
};

function IndustryChart({ userLevel }) {
  const userTier = userLevel <= 2 ? 0 : userLevel === 3 ? 1 : 2;
  return (
    <div style={{ background:B.bg, borderRadius:12, padding:"22px 24px", marginBottom:20 }}>
      <div style={{ fontSize:12, fontWeight:700, color:B.textSub, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4 }}>
        Industry AI task maturity — 2025
      </div>
      <div style={{ fontSize:12, color:B.textSub, marginBottom:20, lineHeight:1.5 }}>
        Based on what tasks legal professionals are actually using AI for today.
      </div>
      {INDUSTRY_TASK_DATA.map((tier, i) => {
        const isUser = i === userTier;
        return (
          <div key={tier.label} style={{ marginBottom: i < INDUSTRY_TASK_DATA.length - 1 ? 16 : 0 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:isUser?700:500, color:isUser?B.navy:B.textMid }}>{tier.label}</span>
                {isUser && (
                  <span style={{ background:B.navy, color:"#fff", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700, letterSpacing:"0.05em" }}>YOU</span>
                )}
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:isUser?B.navy:B.textSub }}>{tier.pct}%</span>
            </div>
            <div style={{ height:10, background:B.border, borderRadius:5, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${tier.pct}%`, background:isUser?B.navy:tier.color, borderRadius:5, transition:"width 0.8s ease" }} />
            </div>
            <div style={{ fontSize:11, color:B.textSub, marginTop:4 }}>{tier.desc}</div>
          </div>
        );
      })}
      <div style={{ fontSize:10, color:"#9CA3AF", marginTop:16, borderTop:`1px solid ${B.border}`, paddingTop:10 }}>
        Source: Litify 2025 State of AI in Legal Report
      </div>
    </div>
  );
}

function ReportCTA() {
  return (
    <div style={{ borderRadius:12, border:`1px solid ${B.border}`, overflow:"hidden", marginBottom:16 }}>
      <img src={REPORT_IMG} alt="State of AI in Legal 2025 Report" style={{ width:"100%", display:"block", maxHeight:200, objectFit:"cover", objectPosition:"top" }} />
      <div style={{ padding:"22px 24px" }}>
        <div style={{ fontSize:11, fontWeight:700, color:B.blue, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:8 }}>Full Report</div>
        <div style={{ fontSize:18, fontWeight:800, color:B.navy, lineHeight:1.3, marginBottom:10, letterSpacing:"-0.01em" }}>
          Explore the Full Report: State of AI in Legal
        </div>
        <p style={{ fontSize:13, color:B.textMid, lineHeight:1.65, marginBottom:18 }}>
          Dive deeper into the research behind your results. The full 2025 report covers AI adoption trends, investment patterns, organizational readiness, and the roadmap to maturity across the legal industry.
        </p>
        <a href={REPORT_URL} target="_blank" rel="noreferrer" style={{ display:"inline-block", background:B.blueBright, color:"#fff", borderRadius:999, padding:"13px 28px", fontSize:14, fontWeight:700, textDecoration:"none", letterSpacing:"0.01em" }}>
          Download the Report →
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);

  // Report height to parent iframe on every phase change and question change
  useHeightReporter(phase === "intro");
  useHeightReporter(phase === "quiz");
  useHeightReporter(phase === "result");

  // Also fire on question changes within the quiz
  useEffect(() => { postHeight(); }, [current]);

  const q = questions[current];
  const totalScore = Object.values(answers).reduce((a,b) => a+b, 0);
  const sectionNames = ["Tools & Investment","Readiness","Task Scope","Impact"];
  const result = levels.find(l => totalScore >= l.range[0] && totalScore <= l.range[1]) || levels[0];
  const progress = phase==="quiz" ? ((current+(selected!==null?1:0))/questions.length)*100 : 0;

  const handleNext = () => {
    if (selected===null) return;
    const newAnswers = {...answers,[current]:selected};
    setAnswers(newAnswers);
    setSelected(null);
    if (current+1 < questions.length) {
      setCurrent(current+1);
    } else {
      // Final question answered — hand off to Marketo gate
      if (window.showMarketoGate) {
        window.showMarketoGate(() => {
          // Marketo form submitted — show results
          setPhase("result");
        });
      } else {
        // Fallback if Marketo didn't load
        setPhase("result");
      }
    }
  };

  const restart = () => { setCurrent(0); setAnswers({}); setSelected(null); setPhase("intro"); };

  // ── INTRO ────────────────────────────────────────────────────────────────
  if (phase==="intro") return (
    <div className="quiz-shell">
      <div className="quiz-card">
        <div style={{ marginBottom:36 }}><LitifyLogo height={30} /></div>
        <div style={{ display:"inline-block", background:B.blueLight, color:B.blueBright, borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:600, marginBottom:18 }}>
          2025 State of AI in Legal
        </div>
        <h1 style={{ fontSize:30, fontWeight:800, color:B.navy, lineHeight:1.2, marginBottom:14, letterSpacing:"-0.02em" }}>
          Benchmark your<br />AI maturity
        </h1>
        <p style={{ fontSize:15, color:B.textMid, lineHeight:1.7, marginBottom:28 }}>
          8 questions. 3 minutes. See where your organization sits on Litify's AI Maturity Scale — and how you compare to the rest of the legal industry.
        </p>
        <div className="intro-sections">
          {sectionNames.map(s => (
            <div key={s} style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", background:B.bg, borderRadius:8, border:`1px solid ${B.border}` }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:sectionMeta[s].color, flexShrink:0 }} />
              <span style={{ fontSize:13, color:B.textMid, fontWeight:500 }}>{s}</span>
            </div>
          ))}
        </div>
        <button className="primary-btn" onClick={() => setPhase("quiz")}>Start the benchmark →</button>
        <p style={{ fontSize:11, color:B.textSub, marginTop:14, textAlign:"center" }}>
          Based on research from the Litify 2025 State of AI in Legal Report
        </p>
      </div>
    </div>
  );

  // ── QUIZ ─────────────────────────────────────────────────────────────────
  if (phase==="quiz") {
    const meta = sectionMeta[q.section];
    return (
      <div className="quiz-shell">
        <div className="quiz-card">
          <div style={{ marginBottom:24 }}><LitifyLogo height={26} /></div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontSize:12, color:B.textSub, fontWeight:500 }}>Question {current+1} of {questions.length}</span>
            <span style={{ fontSize:12, color:B.textSub }}>{Math.round(progress)}% complete</span>
          </div>
          <div style={{ height:4, background:B.border, borderRadius:2, marginBottom:24, overflow:"hidden" }}>
            <div style={{ width:`${progress}%`, height:"100%", background:B.blue, borderRadius:2, transition:"width 0.3s ease" }} />
          </div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:meta.light, color:meta.color, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:16 }}>
            <span>{q.sectionIcon}</span><span>{q.section}</span>
          </div>
          <h2 style={{ fontSize:19, fontWeight:700, color:B.navy, lineHeight:1.4, marginBottom:20, letterSpacing:"-0.01em" }}>
            {q.question}
          </h2>
          <div style={{ display:"flex", flexDirection:"column", gap:9, marginBottom:24 }}>
            {q.options.map((opt,i) => {
              const isSel = selected===opt.score;
              return (
                <button key={i} onClick={() => setSelected(opt.score)} style={{
                  display:"flex", alignItems:"flex-start", gap:12,
                  background:isSel?B.blueLight:"#fff",
                  border:`1.5px solid ${isSel?B.blue:B.border}`,
                  borderRadius:10, padding:"13px 15px",
                  cursor:"pointer", textAlign:"left", transition:"all 0.12s ease", width:"100%",
                }}>
                  <div style={{ width:26, height:26, borderRadius:6, flexShrink:0, background:isSel?B.blue:B.bg, border:`1.5px solid ${isSel?B.blue:B.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:isSel?"#fff":B.textSub, marginTop:1 }}>
                    {String.fromCharCode(65+i)}
                  </div>
                  <span style={{ fontSize:14, color:isSel?B.navy:B.textMid, lineHeight:1.55, fontWeight:isSel?500:400 }}>{opt.text}</span>
                </button>
              );
            })}
          </div>
          <button className="primary-btn" style={{ opacity:selected!==null?1:0.3, cursor:selected!==null?"pointer":"not-allowed" }}
            onClick={handleNext} disabled={selected===null}>
            {current+1===questions.length ? "See my results →" : "Next →"}
          </button>
        </div>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────────────────────
  return (
    <div className="quiz-shell">
      <div className="result-card">
        <div style={{ marginBottom:28 }}><LitifyLogo height={26} /></div>

        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
          <div style={{ background:B.navy, color:"#fff", borderRadius:12, padding:"10px 16px", textAlign:"center", flexShrink:0 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", opacity:0.6, marginBottom:2 }}>Level</div>
            <div style={{ fontSize:26, fontWeight:800, lineHeight:1 }}>{result.level}</div>
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:B.textSub, letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:3 }}>Your AI Maturity</div>
            <div style={{ fontSize:24, fontWeight:800, color:B.navy, letterSpacing:"-0.02em", lineHeight:1 }}>{result.label}</div>
            <div style={{ fontSize:13, color:B.blue, fontWeight:500, marginTop:3 }}>{result.tagline}</div>
          </div>
        </div>

        <div className="level-scale">
          {levels.map(l => (
            <div key={l.level} style={{ background:l.level===result.level?B.navy:B.bg, border:`1.5px solid ${l.level===result.level?B.navy:B.border}`, borderRadius:8, padding:"9px 10px", textAlign:"center" }}>
              <div style={{ fontSize:10, color:l.level===result.level?"rgba(255,255,255,0.6)":B.textSub, fontWeight:600, marginBottom:2 }}>Level {l.level}</div>
              <div style={{ fontSize:13, fontWeight:700, color:l.level===result.level?"#fff":B.textMid }}>{l.label}</div>
            </div>
          ))}
        </div>

        <p style={{ fontSize:14, color:B.textMid, lineHeight:1.7, marginBottom:12 }}>{result.description}</p>

        <div style={{ background:B.blueLight, border:`1px solid ${B.blueMid}`, borderRadius:10, padding:"14px 16px", marginBottom:28 }}>
          <div style={{ fontSize:11, fontWeight:700, color:B.blue, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:5 }}>Your next step</div>
          <p style={{ fontSize:13, color:B.navyMid, lineHeight:1.65, margin:0 }}>{result.next}</p>
        </div>

        <div style={{ fontSize:12, fontWeight:700, color:B.textSub, letterSpacing:"0.07em", textTransform:"uppercase", marginBottom:14 }}>
          How you compare
        </div>
        <IndustryChart userLevel={result.level} />

        <div style={{ borderTop:`1px solid ${B.border}`, margin:"28px 0" }} />
        <ReportCTA />

        <button onClick={restart} style={{ width:"100%", background:"transparent", border:`1px solid ${B.border}`, color:B.textSub, borderRadius:8, padding:"11px", fontSize:13, cursor:"pointer", marginTop:8, fontFamily:"'Inter',sans-serif" }}>
          Retake the quiz
        </button>
      </div>
    </div>
  );
}
